import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { coinGetMe, getRoom, tokenGetMe } from "../services/game.service";
import GameItem from "../components/items/GameItem";
import HeaderBar from "../components/HeaderBar";
import { useNavigate } from "react-router-dom";
import BannerComponent from "../game/components/BannerComponent";
import Loading from "../components/Loading";

type Props = {};

export default function GameV2Page() {
  const navigate = useNavigate(); //

  // call useQuery without assigning unused vars (services read token from sessionStorage)
  const { data: roomData, isLoading: isRoomLoading } = useQuery(
    ["get-room-data"],
    async () => {
      // getRoom() reads token from sessionStorage if not provided
      return await getRoom();
    }
  );

  const { data: tokenMe, isLoading: isTokenLoading } = useQuery(
    ["get-token-me"],
    async () => {
      // getTokenMe() reads token from sessionStorage if not provided
      const res = await tokenGetMe({});
      return res;
    }
  );
  const { data: coinMe, isLoading: isCoinLoading } = useQuery(
    ["get-coin-me"],
    async () => {
      // getCoinMe() reads token from sessionStorage if not provided
      const res = await coinGetMe({});
      return res;
    }
  );

  // preload important images and show loading until both data & assets ready
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const images = [
      "/images/token_box.png",
      "/images/point_box.png",
      "/images/go2spendPoint.png",
      "/images/component.png",
    ];
    if (images.length === 0) {
      setAssetsLoaded(true);
      return;
    }
    let loaded = 0;

    const finish = () => {
      if (!cancelled) setAssetsLoaded(true);
    };

    const timeout = setTimeout(() => {
      // fallback: don't block forever
      if (!cancelled) finish();
    }, 8000);

    images.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loaded++;
        if (!cancelled) setProgress(Math.round((loaded / images.length) * 100));
        if (loaded === images.length) {
          clearTimeout(timeout);
          finish();
        }
      };
      img.src = url;
    });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  const isDataLoading = isRoomLoading || isTokenLoading || isCoinLoading;

  if (isDataLoading || !assetsLoaded) {
    return (
      <div className="relative p-4 overflow-auto h-full">
        <Loading
          progress={progress}
          message={isDataLoading ? "Loading data..." : "Loading assets..."}
        />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <HeaderBar />

      <div className="relative pl-4 pr-4 pb-4 overflow-auto h-full">
        {/* banner */}
        <div className="w-full mb-4">
          <BannerComponent />
        </div>

        {/* button */}
        <div className="w-full flex justify-center items-center mb-4 fixed bottom-0 left-0 z-[2000]">
          <div className="w-[200px]" onClick={() => navigate("/buy-ticket")}>
            <GameItem width={"100%"} itemKey={"buyTicketButton"} />
          </div>
        </div>

        {/* card */}
        <div className="p-[12px] bg-[#ffffff]/60 mb-4 rounded-xl">
          <div className="grid grid-cols-6 gap-1">
            {[...Array(6)].map((_, idx) => (
              <div
                key={idx}
                style={{
                  filter:
                    idx + 1 <= roomData?.ticketInactive?.lv4?.length
                      ? "grayscale(100%) contrast(120%) brightness(90%)"
                      : "none",
                }}
              >
                <GameItem width={"100%"} itemKey={"l4card"} />
              </div>
            ))}
          </div>
        </div>
        {/* Card L3 */}
        <div
          className="bg-[#ffffff]/60 mb-4 rounded-xl"
          style={{
            padding: "10px",
            paddingTop: "9%",
            rowGap: "12%",
            paddingBottom: "4%",
          }}
        >
          <div className="relative">
            <div className="grid grid-cols-8">
              {[...Array(16)].map((_, idx) => (
                <div
                  className="relative w-full aspect-[2/1] grid grid-cols-1"
                  style={{
                    zIndex: 1000 - idx,
                    filter:
                      idx + 1 <= roomData?.ticketInactive?.lv3?.length
                        ? "grayscale(100%) contrast(120%) brightness(90%)"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      aspectRatio: "1 / 1",
                      bottom: 0,
                      width: "100%",
                      left: 0,
                      right: 0,
                      margin: "auto",
                    }}
                  >
                    <GameItem key={idx} width={"100%"} itemKey={"l3card"} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Card L2 */}
        <div
          className="grid grid-cols-2 gap-2 bg-[#ffffff]/60 rounded-xl"
          style={{
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "10px",
            padding: "10px",
            paddingTop: "8%",
            rowGap: "12%",
            paddingBottom: "22%",
          }}
        >
          {["property", "money_bag", "car", "money", "crown", "gift"].map(
            (itemKey, index) => {
              const filterData = roomData?.ticketInactive?.lv2?.filter(
                (e: any) => e.lv2Type === itemKey
              );

              return (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    width: "100%",
                    gap: "2px",
                  }}
                >
                  {[...Array(16)].map((_, idx) => (
                    <div
                      style={{
                        aspectRatio: "2 / 1",
                        position: "relative",
                        zIndex: 1000 - idx,
                        filter:
                          idx + 1 <= filterData?.length
                            ? "grayscale(100%) contrast(120%) brightness(90%)"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          aspectRatio: "1 / 1",
                          bottom: 0,
                          width: "100%",
                          left: 0,
                          right: 0,
                          margin: "auto",
                        }}
                      >
                        <div className="relative w-full h-full flex justify-center items-center">
                          <GameItem
                            key={idx}
                            width={"100%"}
                            height={"100%"}
                            itemKey={itemKey}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
          )}
        </div>
        <div className="h-[400px]" />
      </div>
    </div>
  );
}
